import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchTeamList, setTeamMemberStatus, unlockTeamMember, resetTeamMemberPassword, type TeamMember } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { TeamMemberForm } from './TeamMemberForm';

// Stage 6 (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8): the team roster,
// plus creating/editing an account's role and permissions and activating/
// deactivating an account. Every write goes through the unmodified,
// already-audited appSaveUser/appSetUserStatus — see src/V6Api.js's
// `teamSaveUser` comment for exactly what stays enforced server-side.
export function TeamPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['team'], queryFn: fetchTeamList });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [confirmingStatus, setConfirmingStatus] = useState<string | null>(null);

  const [resetPasswordFor, setResetPasswordFor] = useState<{ username: string; name: string; temporaryPassword: string } | null>(null);

  const statusMutation = useMutation({
    mutationFn: ({ username, status }: { username: string; status: 'Active' | 'Inactive' }) => setTeamMemberStatus(username, status),
    onSuccess: (result) => {
      setConfirmingStatus(null);
      if (result.ok) void queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });

  const unlockMutation = useMutation({
    mutationFn: (username: string) => unlockTeamMember(username),
    onSuccess: (result) => result.ok && void queryClient.invalidateQueries({ queryKey: ['team'] }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (username: string) => resetTeamMemberPassword(username),
    onSuccess: (result) => {
      if (result.ok && result.temporaryPassword && result.username) {
        setResetPasswordFor({ username: result.username, name: result.name || result.username, temporaryPassword: result.temporaryPassword });
        void queryClient.invalidateQueries({ queryKey: ['team'] });
      }
    },
  });

  if (query.isLoading) return <LoadingState label="Loading team…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load the team.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load the team.'} onRetry={() => query.refetch()} />;
  }

  const team = query.data;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Administration"
        title="Team"
        actions={
          <>
            <Link to="/settings/activity" className="text-sm font-semibold text-gold-300 hover:text-gold-200">
              Activity Log
            </Link>
            {!formOpen && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Add Team Member
              </Button>
            )}
          </>
        }
      />

      {formOpen && (
        <TeamMemberForm
          team={team}
          editing={editing}
          onDone={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      )}

      <ul className="space-y-2">
        {(team.users ?? []).map((member) => (
          <li key={member.username} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink-surface p-3.5 shadow-card">
            <div className="min-w-0">
              <p className="truncate font-semibold text-white">
                {member.name} {member.isCurrent && <span className="text-xs font-normal text-slate-500">(you)</span>}
              </p>
              <p className="truncate text-sm text-slate-400">
                {member.roleLabel} · {member.username}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2.5">
              <Badge tone={member.locked ? 'danger' : member.status === 'Active' ? 'success' : 'neutral'}>{member.locked ? 'Locked' : member.status}</Badge>
              <button
                type="button"
                onClick={() => {
                  setEditing(member);
                  setFormOpen(true);
                }}
                className="text-xs font-semibold text-gold-300 hover:text-gold-200"
              >
                Edit
              </button>
              {member.locked && (
                <button
                  type="button"
                  disabled={unlockMutation.isPending}
                  onClick={() => unlockMutation.mutate(member.username)}
                  className="text-xs font-semibold text-gold-300 hover:text-gold-200 disabled:opacity-60"
                >
                  Unlock
                </button>
              )}
              <button
                type="button"
                disabled={resetPasswordMutation.isPending}
                onClick={() => resetPasswordMutation.mutate(member.username)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-200 disabled:opacity-60"
              >
                Reset Password
              </button>
              {member.role !== 'OWNER' && !member.isCurrent && (
                <>
                  {confirmingStatus === member.username ? (
                    <>
                      <span className="text-xs text-slate-500">Confirm?</span>
                      <button
                        type="button"
                        disabled={statusMutation.isPending}
                        onClick={() => statusMutation.mutate({ username: member.username, status: member.status === 'Active' ? 'Inactive' : 'Active' })}
                        className="text-xs font-semibold text-red-300 hover:text-red-200 disabled:opacity-60"
                      >
                        Yes, {member.status === 'Active' ? 'deactivate' : 'activate'}
                      </button>
                      <button type="button" onClick={() => setConfirmingStatus(null)} className="text-xs text-slate-500 hover:text-slate-300">
                        No
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setConfirmingStatus(member.username)} className="text-xs font-semibold text-slate-400 hover:text-slate-200">
                      {member.status === 'Active' ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
      {statusMutation.data && !statusMutation.data.ok && <ErrorState message={statusMutation.data.message || 'Could not update that account.'} />}
      {unlockMutation.data && !unlockMutation.data.ok && <ErrorState message={unlockMutation.data.message || 'Could not unlock that account.'} />}
      {resetPasswordMutation.data && !resetPasswordMutation.data.ok && <ErrorState message={resetPasswordMutation.data.message || 'Could not reset that password.'} />}

      {resetPasswordFor && (
        <Card className="border-success/30">
          <p className="font-semibold text-emerald-200">New temporary password for {resetPasswordFor.name} (shown once):</p>
          <code className="mt-2 block select-all rounded-lg border border-ink-border bg-ink px-3 py-2 text-sm font-mono text-white">{resetPasswordFor.temporaryPassword}</code>
          <Button variant="secondary" onClick={() => setResetPasswordFor(null)} className="mt-3">
            Done
          </Button>
        </Card>
      )}
    </div>
  );
}
